import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClientFormVatTable } from "./client_form/ClientFormVatTable";
import { ClientFxMethods } from "./client_form/ClientFxMethods";   // A2-543
import { ESTABLISHMENT, FILING_MODEL, labelFor } from "@/lib/enumLabels";
import { InfoHint } from "./InfoHint";
import { IT_SIGNER_ROLES, IT_SIGNER_ROLE_LABELS } from "../lib/itSignerRoles";
import { ComplianceRiskBanner } from "./ComplianceRiskBanner";
import { isOssType as _isOssType, normalizeOssDates as _normalizeOssDates } from "./ossDateSync";
import {
  EU_ISO2, EU_ISO2_LIST, EU_GB_CHOICES, REST_OF_WORLD_CHOICES,
  countryOptionLabel, withCurrent,
} from "@/lib/countries";

/**
 * v0.4.38 CP1 — Redesigned client create/edit form.
 *
 * Seven numbered sections with bold headers + border separators:
 *   1. Company details
 *   2. VAT setup (with filing-model dropdown + LOCAL_RETURNS warn)
 *   3. IOSS (checkbox + IM number/MSI country — v0.6.8, split from the
 *      registrations table)
 *   4. OSS (scheme dropdown + MSI/date/number, live UNION_OSS-without-MSI
 *      validation — v0.6.8, split from the registrations table)
 *   5. VAT registrations — LOCAL registrations only (extracted into
 *      ClientFormVatTable)
 *   6. Notes
 *   7. Default supply type (sticky)
 *
 * Inputs sized for actual use — h-11 (44px) with px-4 padding
 * across the form. Per operator instruction "make the blooming
 * boxes bigger!".
 */

// v0.6 UX simplification — these codes used to render verbatim as the
// visible dropdown/badge text (an operator would see "NON_EU_PAN_EU_FBA").
// Plain labels; the underlying value/enum sent to the backend is unchanged.
//
// v0.6.12 — the two label maps used to live HERE, privately, and three other
// render sites therefore never got them: the operator was reading "NON_EU"
// beside their client's name on the calculate screen and in the admin list.
// They now live in lib/enumLabels.js with the rest, and this file imports
// them. The wording is unchanged — it is this file's wording that was
// adopted, because it is the wording operators already read in the form.
const SE = Object.keys(ESTABLISHMENT);
const SE_LABEL = ESTABLISHMENT;
const FILING_MODELS = ["", ...Object.keys(FILING_MODEL)];
const FILING_MODEL_LABEL = FILING_MODEL;
// v0.6.8 fix — the backend places NO restriction on
// establishment_country beyond "2-letter code" (ClientProfileCreate/
// Update._require_establishment_country / _validate_establishment_
// country) and explicitly models non-EU sellers via seller_
// establishment="NON_EU"/"NON_EU_PAN_EU_FBA" -- a China-based Pan-EU
// FBA seller is a core, intended use case, not an edge case. This
// list used to be EU-27 + GB only, silently blocking onboarding of
// any client established outside that set (found live 2026-09-07:
// an operator had real China-based clients with nowhere to put them).
// Expanded to the full ISO-3166-1 alpha-2 set so the picker can never
// again be missing a real client's home country.
//
// v0.6.12 A5-27 — EU_ISO2, EU_GB_CHOICES and REST_OF_WORLD_CHOICES now
// live in lib/countries.js, so the registrations table, the add-
// registration dialog and the work-country resolver offer the same list
// instead of a bare two-letter text box.
const COUNTRY_CHOICES = [
  "", ...EU_GB_CHOICES, ...REST_OF_WORLD_CHOICES,
];
// "DE — Germany" for the two member-state-of-identification pickers.
const EU_MSI_LABELS = Object.fromEntries(
  EU_ISO2_LIST.map((cc) => [cc, countryOptionLabel(cc)]),
);

const emptyClient = () => ({
  code: "", name: "",
  company_legal_name: "", company_registration_number: "",
  establishment_country: "", contact_name: "", contact_email: "",
  // v0.6.8 fix — found live 2026-09-07: every NEW client defaulted to
  // filing_model="UNION_OSS", which _validate_filing_model_consistency
  // (routes_clients.py) then requires oss_msi_country to be set for —
  // even for a client that only does IOSS (or nothing OSS-related at
  // all). The backend already treats filing_model as fully optional
  // (None triggers no consistency check); only the frontend forced a
  // choice that doesn't apply to every client.
  seller_establishment: "EU", filing_model: "",
  company_vat_numbers: [], ioss_im_number: "", ioss_msi_country: "",
  oss_msi_country: "", oss_registration_date: "", oss_registration_number: "",
  uk_vat_frequency: "QUARTERLY", uk_annual_year_end_month: 12, uk_stagger_group: 1,
  local_return_frequency: {},
  local_return_deadline_day: {}, fx_methods: {},
  default_supply_type: "",
  notes: "",
  compliance_findings: [],
});

const READ_ONLY_KEYS = new Set([
  "id", "is_default", "created_at", "updated_at", "created_by",
  "derived_filing_model",
]);

const stripReadOnly = (form) => {
  const out = {};
  for (const k of Object.keys(form)) {
    if (!READ_ONLY_KEYS.has(k)) out[k] = form[k];
  }
  return out;
};

// v0.6.8 fix — found live 2026-09-07: an operator creating an
// IOSS-only client hit "country_code: String should have at least 2
// characters / vat_number: String should have at least 4 characters"
// on save. Cause: checking "This client uses the IOSS scheme"
// (setIossEnabled below) pushes a MARKER entry into
// company_vat_numbers with country_code/vat_number left permanently
// blank -- IOSS's real identifying numbers live in the separate
// ioss_im_number/ioss_msi_country fields, filled in AFTER the
// checkbox is ticked (the fields only render once iossEnabled is
// true). That entry is never rebuilt once the operator actually types
// those numbers in, so it hits the shared VatRegistration schema
// (min_length=2/4, written for real per-country LOCAL/OSS
// registrations) still blank. Mirrors setOssType's existing pattern
// for the OSS entry: rebuild the marker fresh from the current
// top-level fields at the one save choke-point, instead of trying to
// keep it in sync across every field's own onChange.
const _syncIossEntry = (form) => {
  const regs = (form.company_vat_numbers || []).filter(
    (e) => (e.registration_type || "").toUpperCase() !== "IOSS");
  const cc = (form.ioss_msi_country || "").trim().toUpperCase();
  const num = (form.ioss_im_number || "").trim();
  if (!cc && !num) return { ...form, company_vat_numbers: regs };
  return {
    ...form,
    company_vat_numbers: [
      ...regs,
      { country_code: cc, vat_number: num, registration_type: "IOSS",
        registration_date: "" },
    ],
  };
};

const _finalizeForSave = (form) => stripReadOnly(_syncIossEntry(form));


// The parts of the form a link may open at (?section=<name>).
const SECTION_ANCHORS = {
  oss: "#client-form-section-oss",
  ioss: "#client-form-section-ioss",
  vatregs: "#client-form-section-vatregs",
  establishment: '[data-testid="client-form-establishment-country"]',
  filing: '[data-testid="client-form-filing-model"]',
};

export const ClientEditForm = ({ initial, onCancel, onSave }) => {
  const { t } = useTranslation();
  const isNew = !initial;
  const [form, setForm] = useState(() =>
    initial ? _normalizeOssDates({ ...emptyClient(), ...initial }) : emptyClient());
  const [error, setError] = useState(null);
  const [pendingCodeChange, setPendingCodeChange] = useState(null);
  const [overrideEst, setOverrideEst] = useState(false);
  const [iossOpen, setIossOpen] = useState(false);

  // ?section=<name> scrolls to that part of the form and rings it for a few
  // seconds. v0.6.8 — oss and ioss (each an always-visible section);
  // A2-582 — any part a review card's "fix it in the client record" button
  // names (api/_review_answers GO_TO client.*), one effect instead of one per
  // section.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const name = new URLSearchParams(window.location.search).get("section");
    const sel = SECTION_ANCHORS[name];
    if (!sel) return;
    if (name === "ioss") setIossOpen(true);
    const el = document.querySelector(sel);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-amber-400/60", "rounded-md");
    const id = setTimeout(() => {
      el.classList.remove("ring-2", "ring-amber-400/60", "rounded-md");
    }, 3500);
    return () => clearTimeout(id);
  }, []);

  // A1-46 — the Codice Fiscale box is Italy-only; key it off the
  // registrations the operator has actually entered.
  const hasItalianRegistration = (form.company_vat_numbers || []).some(
    (r) => ((r || {}).country_code || "").toUpperCase() === "IT",
  );

  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  // v0.6.12 — a blank box means "use the default", so it must DELETE the key
  // rather than store "" — an empty string would survive a round-trip and read
  // as a deliberate value.
  const setLocalDeadlineDay = (cc, v) => {
    const next = { ...(form.local_return_deadline_day || {}) };
    const s = String(v ?? "").trim();
    if (s === "") delete next[cc];
    else next[cc] = Number(s);
    f("local_return_deadline_day", next);
  };
  const setLocalFrequency = (cc, v) => f("local_return_frequency", {
    ...(form.local_return_frequency || {}), [cc]: v,
  });
  const setReg = (i, k, v) => {
    const r = [...(form.company_vat_numbers || [])];
    r[i] = { ...r[i], [k]: v };
    // v0.6 — single source of truth for the OSS date: switching a row TO an
    // OSS type immediately adopts oss_registration_date, so there is never a
    // moment where the row's own date can independently disagree.
    if (k === "registration_type" && _isOssType(v)) {
      r[i] = { ...r[i], registration_date: form.oss_registration_date || "" };
    }
    f("company_vat_numbers", r);
  };
  // v0.6 — the ONE place the OSS date of record is set. Changing it here
  // propagates to every OSS row's registration_date in the same update, so
  // the table cell and the OSS detail field can never drift apart (this
  // replaces the earlier "keep both, warn on mismatch" approach — with no
  // real client history to preserve, there is no reason to allow two
  // independently-editable copies of the same fact).
  //
  // v0.6.8 — generalized beyond just the date: OSS now has its own
  // dedicated section (not an inline table-row expansion), so
  // oss_msi_country/oss_registration_number are edited directly there
  // too and must sync into the underlying registration entry the same
  // way the date already did — otherwise the export logic (which reads
  // country_code/vat_number off the ARRAY entry) would silently see
  // stale data the moment an operator edited the top-level field.
  const OSS_FIELD_TO_ARRAY_KEY = {
    oss_registration_date: "registration_date",
    oss_msi_country: "country_code",
    oss_registration_number: "vat_number",
  };
  const setOssField = (k, v) => {
    const arrayKey = OSS_FIELD_TO_ARRAY_KEY[k];
    setForm((s) => ({
      ...s, [k]: v,
      company_vat_numbers: arrayKey
        ? (s.company_vat_numbers || []).map((r) =>
            _isOssType(r.registration_type) ? { ...r, [arrayKey]: v } : r)
        : s.company_vat_numbers,
    }));
  };
  const addReg = () => f("company_vat_numbers",
    [...(form.company_vat_numbers || []),
     { country_code: "", vat_number: "", registration_type: "LOCAL",
       registration_date: "" }]);
  const rmReg = (i) => f("company_vat_numbers",
    (form.company_vat_numbers || []).filter((_, idx) => idx !== i));

  // v0.6.8 — IOSS and OSS split out of the VAT registrations table into
  // their own dedicated sections (operator: "there is IOSS setting
  // above and then again you have option that allows to select IOSS
  // scheme among VAT registrations, this makes no sense"). Both still
  // store as entries in the SAME underlying company_vat_numbers array
  // (no backend/classify.py change needed, no data migration) — these
  // just replace the table row + Type dropdown as the UI that manages
  // them, presented as what they actually are: a single yes/no flag
  // (IOSS) and a single scheme choice (OSS), not a general-purpose
  // per-country registration list entry.
  const iossEnabled = (form.company_vat_numbers || []).some(
    (e) => (e.registration_type || "").toUpperCase() === "IOSS");
  const setIossEnabled = (enabled) => {
    const regs = form.company_vat_numbers || [];
    const has = regs.some((e) => (e.registration_type || "").toUpperCase() === "IOSS");
    if (enabled === has) return;
    f("company_vat_numbers", enabled
      ? [...regs, { country_code: "", vat_number: "", registration_type: "IOSS", registration_date: "" }]
      : regs.filter((e) => (e.registration_type || "").toUpperCase() !== "IOSS"));
  };
  const OSS_TYPES = new Set(["OSS_UNION", "OSS_NON_UNION", "OSS"]);
  const ossEntry = (form.company_vat_numbers || []).find(
    (e) => OSS_TYPES.has((e.registration_type || "").toUpperCase()));
  const ossType = ossEntry ? (ossEntry.registration_type || "").toUpperCase() : "NONE";
  const setOssType = (value) => {
    const regs = (form.company_vat_numbers || []).filter(
      (e) => !OSS_TYPES.has((e.registration_type || "").toUpperCase()));
    if (value !== "NONE") {
      // v0.6.8 fix — found live 2026-09-06: this used to source
      // country_code/vat_number ONLY from the top-level form fields,
      // which — for a legacy client — can still be blank even when the
      // existing OSS entry (ossEntry, computed above) carries the real
      // value. Switching the scheme dropdown (even Union -> Non-Union,
      // not just toggling OSS off) rebuilt the entry from those blank
      // fields and permanently discarded the real MSI country/number.
      // normalizeOssDates now backfills the top-level fields on load
      // (see ossDateSync.js), but falling back to ossEntry here too is
      // defense-in-depth against the same class of bug recurring.
      regs.push({
        country_code: (form.oss_msi_country || ossEntry?.country_code || "").toUpperCase(),
        vat_number: form.oss_registration_number || ossEntry?.vat_number || "",
        registration_type: value,
        registration_date: form.oss_registration_date || ossEntry?.registration_date || "",
      });
    }
    f("company_vat_numbers", regs);
  };

  // v0.6 item 1 — acknowledge ONE country's finding + persist. Per decision
  // #1, acknowledging FR must NOT mark ES/IT/NL handled.
  const acknowledgeOne = async (cc) => {
    const now = new Date().toISOString();
    const next = (form.compliance_findings || []).map((x) =>
      (x.country_code === cc && !x.acknowledged)
        ? { ...x, acknowledged: true, acknowledged_at: now }
        : x);
    const updated = { ...form, compliance_findings: next };
    setForm(updated);
    try { await onSave(_finalizeForSave(updated), false); }
    catch (e) { setError(e?.response?.data?.detail?.message || e.message); }
  };
  // v0.6 item 1 — "Add registration": prefill a LOCAL row for the flagged
  // country and jump to the registrations table.
  // v0.6.8 — was scrolling to "client-form-section-oss", which now
  // names the dedicated OSS section after the split below; this adds a
  // LOCAL row, so it needs the VAT-registrations table's own id.
  const addRegistrationFor = (cc) => {
    f("company_vat_numbers", [
      ...(form.company_vat_numbers || []),
      { country_code: (cc || "").toUpperCase(), vat_number: "",
        registration_type: "LOCAL", registration_date: "" },
    ]);
    if (typeof document !== "undefined") {
      const el = document.getElementById("client-form-section-vatregs");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const submit = async () => {
    setError(null);
    if (!isNew && initial.code !== form.code && !pendingCodeChange) {
      setPendingCodeChange(form.code);
      return;
    }
    try { await onSave(_finalizeForSave(form), isNew); }
    catch (e) {
      setError(e?.response?.data?.detail?.message || e.message || String(e));
    }
  };

  const localReturnsWarn = form.filing_model === "LOCAL_RETURNS";
  const ossUnionNeedsMsi =
    (form.filing_model === "UNION_OSS" || form.filing_model === "HYBRID")
    && !(form.oss_msi_country || "").trim();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-6 py-8" data-testid="client-edit-form">
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
          {isNew ? t("ui.admin_clients.form.title_new", { defaultValue: "New entity" }) : t("ui.admin_clients.form.title_edit", { code: initial.code, name: initial.name, defaultValue: `Edit ${initial.code} · ${initial.name}` })}
        </h1>
        {error && (
          <div data-testid="client-form-error" className="text-rose-700 dark:text-rose-300 text-sm bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {pendingCodeChange && (
          <CodeRenameModal
            from={initial.code} to={form.code}
            onConfirm={async () => {
              try { await onSave(_finalizeForSave(form), false); }
              catch (e) {
                setError(e?.response?.data?.detail?.message || e.message);
                setPendingCodeChange(null);
              }
            }}
            onCancel={() => setPendingCodeChange(null)}
          />
        )}

        <ComplianceRiskBanner
          findings={form.compliance_findings}
          onAcknowledge={acknowledgeOne}
          onAddRegistration={addRegistrationFor}
        />

        {/* 1 Company details */}
        <FormSection num="1" title={t("ui.admin_clients.form.section_company", { defaultValue: "Company details" })}>
          <Grid>
            <Input required label={t("ui.admin_clients.form.trading_name", { defaultValue: "Trading name" })} value={form.name}
                   onChange={(v) => f("name", v)} testid="client-form-name" />
            <Input required label={t("ui.admin_clients.form.code_label", { defaultValue: "Code (2-6 alphanumeric)" })} value={form.code}
                   onChange={(v) => f("code", v.toUpperCase())}
                   hint={t("ui.admin_clients.form.code_hint", { defaultValue: "A short internal reference code for this client (e.g. an abbreviation), not their official registration number — that goes in Company registration number below." })}
                   maxLength={6}
                   testid="client-form-code" />
            <Input label={t("ui.admin_clients.form.legal_name", { defaultValue: "Legal name" })} value={form.company_legal_name || ""}
                   onChange={(v) => f("company_legal_name", v)}
                   testid="client-form-legal-name" />
            <Input label={t("ui.admin_clients.form.company_reg_number", { defaultValue: "Company registration number" })}
                   value={form.company_registration_number || ""}
                   onChange={(v) => f("company_registration_number", v)}
                   testid="client-form-reg-number" />
            {/* A1-46 — shown only once an IT registration exists, because
                it is needed for exactly one thing: the Italian LIPE XML.
                There is no Partita IVA box on purpose: for Italy the VAT
                number IS the partita IVA, so it is read from the IT
                registration below rather than typed twice. */}
            {hasItalianRegistration ? (
              <Input label={t("ui.admin_clients.form.codice_fiscale", { defaultValue: "Codice Fiscale (Italy)" })}
                     value={form.codice_fiscale || ""}
                     onChange={(v) => f("codice_fiscale", v)}
                     hint={t("ui.admin_clients.form.codice_fiscale_hint", { defaultValue: "Required to generate the Italian LIPE XML. Often the same 11 digits as the partita IVA, but not always — and 16 characters for a sole trader. The partita IVA itself is taken from the IT VAT registration, so it is not asked for twice." })}
                     maxLength={16}
                     testid="client-form-codice-fiscale" />
            ) : null}
            {/* Roadmap 1b.9 (A2-305) — who signs the LIPE. Specifiche tecniche,
                Frontespizio: CFDichiarante "obbligatorio per i contribuenti
                diversi dalle persone fisiche"; the role is a code from the
                Modello IVA 2026 "Tabella generale dei codici di carica", as
                the LIPE schema admits them (10 is not). Picked, not typed. */}
            {hasItalianRegistration ? (
              <>
                <Input label={t("ui.admin_clients.form.it_signer_cf", { defaultValue: "LIPE signer — Codice Fiscale" })}
                       value={form.it_signer_codice_fiscale || ""}
                       onChange={(v) => f("it_signer_codice_fiscale", v)}
                       hint={t("ui.admin_clients.form.it_signer_cf_hint", { defaultValue: "The person who signs the Italian LIPE for the company (16 characters). Required for a company; a sole trader signs for themselves and can leave it blank." })}
                       maxLength={16}
                       testid="client-form-it-signer-cf" />
                <Select label={t("ui.admin_clients.form.it_signer_role", { defaultValue: "LIPE signer — role" })}
                        value={form.it_signer_role || ""}
                        options={IT_SIGNER_ROLES}
                        labels={IT_SIGNER_ROLE_LABELS}
                        onChange={(v) => f("it_signer_role", v)}
                        testid="client-form-it-signer-role" />
                {/* A2-446 — CodiceFiscaleSocieta (lipe.txt L489-495). */}
                <Input label={t("ui.admin_clients.form.it_signer_company_cf", { defaultValue: "LIPE — filing company's Codice Fiscale" })}
                       value={form.it_signer_company_cf || ""}
                       onChange={(v) => f("it_signer_company_cf", v)}
                       hint={t("ui.admin_clients.form.it_signer_company_cf_hint", { defaultValue: "Only when a company files the LIPE on the client's behalf, for example as its fiscal representative (11 digits). Leave blank otherwise." })}
                       maxLength={11}
                       testid="client-form-it-signer-company-cf" />
              </>
            ) : null}
            <Select label={t("ui.admin_clients.form.establishment_country", { defaultValue: "Establishment country" })} required
                    value={form.establishment_country || ""}
                    options={COUNTRY_CHOICES}
                    onChange={(v) => setForm((s) => {
                      const cc = (v || "").toUpperCase();
                      const next = { ...s, establishment_country: v };
                      // v0.5.9h — keep seller establishment consistent with
                      // the entity's home country. A non-EU establishment
                      // (e.g. GB) can't be an EU seller; auto-switch EU↔NON_EU
                      // but never touch the Pan-EU FBA choice.
                      if (cc && !EU_ISO2.has(cc) && s.seller_establishment === "EU") {
                        next.seller_establishment = "NON_EU";
                      } else if (cc && EU_ISO2.has(cc) && s.seller_establishment === "NON_EU") {
                        next.seller_establishment = "EU";
                      }
                      return next;
                    })}
                    testid="client-form-establishment-country" />
            <DerivedEstablishment
              value={form.seller_establishment}
              establishmentCountry={form.establishment_country}
              override={overrideEst}
              onOverride={() => setOverrideEst(true)}
              onChange={(v) => f("seller_establishment", v)}
            />
            <Input label={t("ui.admin_clients.form.contact_name", { defaultValue: "Contact name" })} value={form.contact_name || ""}
                   onChange={(v) => f("contact_name", v)}
                   testid="client-form-contact-name" />
            <Input label={t("ui.admin_clients.form.contact_email", { defaultValue: "Contact email" })} type="email"
                   value={form.contact_email || ""}
                   onChange={(v) => f("contact_email", v)}
                   testid="client-form-contact-email" />
          </Grid>
        </FormSection>

        {/* 2 VAT setup */}
        <FormSection num="2" title={t("ui.admin_clients.form.section_vat_setup", { defaultValue: "VAT setup" })}>
          <Grid>
            <Select label={t("ui.admin_clients.form.filing_model", { defaultValue: "Filing model" })} value={form.filing_model || ""}
                    options={FILING_MODELS} labels={FILING_MODEL_LABEL}
                    onChange={(v) => f("filing_model", v)}
                    testid="client-form-filing-model" />
          </Grid>
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed" data-testid="filing-model-explainer">
            {t("ui.admin_clients.form.filing_model_explainer_before", { defaultValue: "How this client's cross-border sales get filed — via the EU-wide OSS scheme, separate local returns per country, or a mix. This is a filing-strategy choice and is" })} <span className="font-semibold">{t("ui.admin_clients.form.filing_model_explainer_em", { defaultValue: "independent of where the seller is established" })}</span> {t("ui.admin_clients.form.filing_model_explainer_after", { defaultValue: "(set in Company details above)." })}
          </p>
          {form.filing_model === "HYBRID" && (
            <Note testid="filing-model-hybrid-note" tone="slate">
              {t("ui.admin_clients.form.hybrid_note", { defaultValue: "HYBRID per-country override is coming soon. For now, the engine treats HYBRID the same as UNION_OSS: rows route to OSS when an OSS_UNION registration is present." })}
            </Note>
          )}
          {localReturnsWarn && (
            <Note testid="filing-model-local-returns-warn" tone="amber">
              {t("ui.admin_clients.form.local_returns_warn", { defaultValue: "Confirm your accountant has advised this model — Union OSS is the default for most cross-border B2C sellers." })}
            </Note>
          )}
        </FormSection>

        {/* 3 IOSS filing details — collapsed to one line when unconfigured.
            v0.6.8 clarity pass — used to be titled just "IOSS", the same
            word as the "IOSS" registration_type option that used to live
            in the VAT registrations table below, with nothing
            distinguishing them. Operator-reported 2026-09-04: added an
            "IOSS" registration row there, believed that satisfied "the
            IOSS number", then hit a silent block on the Export filing
            page because THIS section — a completely separate pair of
            fields the engine actually reads for the IOSS XML export —
            was still empty. Split fully in this pass: the checkbox below
            is now the ONE place that tells the classification engine
            this client's transactions use the IOSS scheme (previously a
            row in the registrations table); these two fields remain the
            identifying numbers needed to file the return itself. */}
        <div id="client-form-section-ioss">
        <FormSection num="3" title="IOSS">
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            {t("ui.admin_clients.form.ioss_intro", { defaultValue: "Whether this client uses the IOSS scheme, and the identifying numbers needed to generate the IOSS XML export. Separate from OSS and from per-country VAT registrations below." })}
          </p>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" data-testid="client-form-ioss-enabled"
                   checked={iossEnabled}
                   onChange={(e) => setIossEnabled(e.target.checked)}
                   className="h-4 w-4 rounded border-slate-300 dark:border-slate-700" />
            {t("ui.admin_clients.form.ioss_enabled", { defaultValue: "This client uses the IOSS scheme" })}
          </label>
          {(!iossOpen && !(form.ioss_im_number || form.ioss_msi_country)) && (
            <Note testid="ioss-not-configured-note" tone="amber">
              {t("ui.admin_clients.form.ioss_not_configured", { defaultValue: "Not configured for this client — the Export filing page will block the IOSS XML download until both fields below are set." })}{" "}
              <button
                type="button"
                data-testid="client-form-ioss-configure"
                onClick={() => setIossOpen(true)}
                className="underline decoration-dotted hover:text-amber-700 dark:hover:text-amber-100"
              >
                {t("ui.admin_clients.form.ioss_configure_now", { defaultValue: "+ configure now" })}
              </button>
            </Note>
          )}
          <Grid>
            <Input label="IOSS IM number" value={form.ioss_im_number || ""}
                   onChange={(v) => f("ioss_im_number", v)}
                   hint={t("ui.admin_clients.form.ioss_im_hint", { defaultValue: "Format: IM followed by exactly 10 digits, e.g. IM4020000010." })}
                   placeholder="IM4020000010"
                   testid="client-form-ioss" />
            {/* v0.6.12 A5-27 — an EU member state is picked, not typed. */}
            <Select label={t("ui.admin_clients.form.ioss_msi_country", { defaultValue: "IOSS MSI country" })}
                    value={(form.ioss_msi_country || "").toUpperCase()}
                    options={withCurrent(["", ...EU_ISO2_LIST], form.ioss_msi_country)}
                    labels={EU_MSI_LABELS}
                    onChange={(v) => f("ioss_msi_country", v.toUpperCase())}
                    testid="client-form-ioss-msi" />
          </Grid>
        </FormSection>
        </div>

        {/* 4 OSS — split out of the VAT registrations table (was an
            expandable OSS_UNION row there) into its own always-visible
            section, matching the IOSS split above. Still stored as an
            entry in the same company_vat_numbers array (setOssType/
            setOssField), so no backend/classify.py change was needed. */}
        <div id="client-form-section-oss">
        <FormSection num="4" title="OSS">
          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
            {t("ui.admin_clients.form.oss_intro", { defaultValue: "Whether this client files through the EU-wide OSS scheme, and which variant. Separate from IOSS above and from per-country VAT registrations below." })}
          </p>
          <Grid>
            <Select label={t("ui.admin_clients.form.oss_scheme", { defaultValue: "OSS scheme" })} value={ossType}
                    options={["NONE", "OSS_UNION", "OSS_NON_UNION"]}
                    labels={{ NONE: t("ui.admin_clients.form.oss_not_used", { defaultValue: "Not used" }), OSS_UNION: "Union OSS", OSS_NON_UNION: "Non-Union OSS" }}
                    onChange={setOssType}
                    testid="client-form-oss-type" />
          </Grid>
          {/* v0.6.12 (A2-85) — last year's cross-border B2C total.
              Art 59c(1)(c) gives origin taxation only while EUR 10,000
              was not exceeded in the current year NOR the preceding one.
              The engine reads a frozen prior calc for that; a client in
              its first year here has none, and without an answer every
              cross-border B2C digital row is held with no way to
              acknowledge it. This is the one fact only the client has. */}
          <Grid>
            <Input label={t("ui.admin_clients.form.preceding_b2c", { defaultValue: "Last year's cross-border B2C sales (EUR)" })}
                   value={form.preceding_year_b2c_cross_border_eur || ""}
                   onChange={(v) => f("preceding_year_b2c_cross_border_eur", v)}
                   hint={t("ui.admin_clients.form.preceding_b2c_hint", { defaultValue: "Total sold to consumers in other EU countries that year, in euro. Only needed before this client has a full year filed here; a filed year always wins. Leave blank if you do not know \u2014 the engine holds those rows rather than assuming." })}
                   placeholder={t("ui.admin_clients.form.preceding_b2c_placeholder", { defaultValue: "e.g. 8500" })}
                   testid="client-form-preceding-year-b2c" />
            <Input label={t("ui.admin_clients.form.preceding_b2c_year", { defaultValue: "…which calendar year" })} type="number"
                   value={form.preceding_year_b2c_year || ""}
                   onChange={(v) => f("preceding_year_b2c_year", v ? Number(v) : null)}
                   hint={t("ui.admin_clients.form.preceding_b2c_year_hint", { defaultValue: "The year that figure describes. It is used only for the year immediately before the period you calculate." })}
                   placeholder="2025"
                   testid="client-form-preceding-year-year" />
          </Grid>
          {/* A2-491 — a UK client's live online events to EU consumers: UK or EU law, the client picks. */}
          {(form.establishment_country || "").toUpperCase() === "GB" && (<Grid>
            <Select label={t("ui.admin_clients.form.online_event_vat")} value={form.uk_online_event_eu_consumer_vat || ""}
                    options={["", "UK", "CUSTOMER_COUNTRY"]} onChange={(v) => f("uk_online_event_eu_consumer_vat", v || null)}
                    labels={{ "": t("ui.admin_clients.form.online_event_vat_unset"), UK: t("ui.admin_clients.form.online_event_vat_UK"), CUSTOMER_COUNTRY: t("ui.admin_clients.form.online_event_vat_CUSTOMER_COUNTRY") }}
                    testid="client-form-online-event-vat" />
            <p className="text-xs text-slate-600 dark:text-slate-400 self-end">{t("ui.admin_clients.form.online_event_vat_hint")}</p>
          </Grid>)}
          {ossType !== "NONE" && (
            <Grid>
              <Select label={t("ui.admin_clients.form.oss_msi_country", { defaultValue: "OSS MSI country" })}
                      value={(form.oss_msi_country || "").toUpperCase()}
                      options={withCurrent(["", ...EU_ISO2_LIST], form.oss_msi_country)}
                      labels={EU_MSI_LABELS}
                      onChange={(v) => setOssField("oss_msi_country", v.toUpperCase())}
                      testid="client-form-oss-msi" />
              <Input label={t("ui.admin_clients.form.oss_reg_date", { defaultValue: "OSS registration date" })} type="date"
                     value={form.oss_registration_date || ""}
                     onChange={(v) => setOssField("oss_registration_date", v)}
                     testid="client-form-oss-date" />
              <Input label={t("ui.admin_clients.form.oss_reg_number", { defaultValue: "OSS registration number" })}
                     value={form.oss_registration_number || ""}
                     onChange={(v) => setOssField("oss_registration_number", v)}
                     placeholder={t("ui.admin_clients.form.optional", { defaultValue: "optional" })}
                     testid="client-form-oss-number" />
            </Grid>
          )}
          {ossUnionNeedsMsi && (
            <Note testid="oss-msi-required-warn" tone="amber">
              {/* v0.6.12 — printed the raw code: "required for HYBRID
                  filing". Found by rule6 once seller_establishment and
                  filing_model joined ENUM_FIELDS, in the very file that
                  owned the label map. */}
              {t("ui.admin_clients.form.oss_country_required", { model: labelFor("filing_model", form.filing_model), defaultValue: `OSS registration country required for ${labelFor("filing_model", form.filing_model)} filing — set the OSS MSI country above.` })}
            </Note>
          )}
        </FormSection>
        </div>

        {/* 5 VAT registrations — per-country LOCAL registrations only,
            now that IOSS/OSS have their own sections above. */}
        <div id="client-form-section-vatregs">
        <FormSection num="5" title={t("ui.admin_clients.form.section_vat_regs", { defaultValue: "VAT registrations" })}>
          <ClientFormVatTable
            rows={form.company_vat_numbers}
            setReg={setReg} addReg={addReg} rmReg={rmReg}
            ukVatFrequency={form.uk_vat_frequency}
            setUkVatFrequency={(v) => f("uk_vat_frequency", v)}
            ukAnnualYearEndMonth={form.uk_annual_year_end_month}
            setUkAnnualYearEndMonth={(v) => f("uk_annual_year_end_month", v)}
            ukStaggerGroup={form.uk_stagger_group}
            setUkStaggerGroup={(v) => f("uk_stagger_group", v)}
            localReturnFrequency={form.local_return_frequency}
            localDeadlineDay={form.local_return_deadline_day}
            setLocalDeadlineDay={setLocalDeadlineDay}
            setLocalFrequency={setLocalFrequency}
          />
        </FormSection>
        </div>

        {/* 6 Notes — A2-571 (operator 2026-10-27: "We should not give
            options of free text only select dropdown"): notes already on
            file stay visible, read-only; nothing new is typed here. */}
        <FormSection num="6" title={t("ui.admin_clients.form.section_notes", { defaultValue: "Notes" })}>
          {(form.notes || "").trim() ? (
            <details data-testid="client-form-notes-details" className="group">
              <summary className="text-xs text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 select-none">
                {t("ui.admin_clients.form.notes_summary_readonly", { defaultValue: "Notes already on file — read-only" })}
              </summary>
              <p data-testid="client-form-notes"
                 className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4 rounded-md">
                {form.notes}
              </p>
            </details>
          ) : (
            <p data-testid="client-form-notes-none" className="text-xs text-slate-500 dark:text-slate-400">
              {t("ui.admin_clients.form.notes_none", { defaultValue: "No notes on file." })}
            </p>
          )}
        </FormSection>

        {/* 7 sticky default supply type — applied by the adapter to rows whose
            `Supply type` cell is blank; the upload page only reports it (A2-69). */}
        <FormSection num="7" title={t("ui.admin_clients.form.default_supply_type_title", { defaultValue: "Default supply type (sticky)" })}>
          <div className="space-y-2">
            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              {t("ui.admin_clients.form.default_supply_type_help_before", { defaultValue: "Used for every uploaded row whose" })}{" "}<span className="font-semibold">Supply type</span>{" "}{/* i18n-ignore: the template's column name stays English */}
              {t("ui.admin_clients.form.default_supply_type_help_after", { defaultValue: "cell is left blank. A filled cell always wins. The upload page shows which default is in force; there is no upload-time control to change it." })}
            </p>
            <select
              data-testid="client-form-default-supply-type"
              value={form.default_supply_type || ""}
              onChange={(e) => f("default_supply_type", e.target.value)}
              className="w-full md:w-72 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-3 py-2 text-sm rounded-md"
            >
              <option value="">{t("ui.admin_clients.form.default_supply_type_none", { defaultValue: "— no sticky default —" })}</option>
              <option value="GOODS">{t("supply_types.GOODS", { defaultValue: "Goods" })}</option>
              <option value="PROFESSIONAL_SERVICE">{t("supply_types.PROFESSIONAL_SERVICE", { defaultValue: "Professional service (Art 44)" })}</option>
              <option value="DIGITAL_SERVICE">{t("supply_types.DIGITAL_SERVICE", { defaultValue: "Digital service (Art 58)" })}</option>
              <option value="OTHER_SERVICE">{t("supply_types.OTHER_SERVICE", { defaultValue: "Other service" })}</option>
              <option value="IMMOVABLE_PROPERTY_SERVICE">{t("supply_types.IMMOVABLE_PROPERTY_SERVICE", { defaultValue: "Immovable-property service (Art 47)" })}</option>
              <option value="EVENT_ADMISSION">{t("supply_types.EVENT_ADMISSION", { defaultValue: "Event admission (Art 53)" })}</option>
              <option value="RESTAURANT_CATERING">{t("supply_types.RESTAURANT_CATERING", { defaultValue: "Restaurant / catering (Art 55)" })}</option>
              <option value="TRANSPORT_SERVICE">{t("supply_types.TRANSPORT_SERVICE", { defaultValue: "Transport service" })}</option>
              <option value="VEHICLE_HIRE_SHORT_TERM">{t("supply_types.VEHICLE_HIRE_SHORT_TERM", { defaultValue: "Short-term vehicle hire (Art 56)" })}</option>
              <option value="VEHICLE_HIRE_LONG_TERM">{t("supply_types.VEHICLE_HIRE_LONG_TERM", { defaultValue: "Long-term vehicle hire (Art 56)" })}</option>
              <option value="WORK_ON_MOVABLE_PROPERTY">{t("supply_types.WORK_ON_MOVABLE_PROPERTY", { defaultValue: "Work on movable property" })}</option>
              <option value="INTERMEDIARY_SERVICE">{t("ui.admin_clients.form.supply_type_option.INTERMEDIARY_SERVICE", { defaultValue: "Intermediary / agency service (Art 46)" })}</option>
              {/* v0.6.11 — PASSENGER_TRANSPORT / GOODS_TRANSPORT were
                  valid supply types that this list never offered. */}
              <option value="PASSENGER_TRANSPORT">{t("supply_types.PASSENGER_TRANSPORT", { defaultValue: "Passenger transport (Art 48)" })}</option>
              <option value="GOODS_TRANSPORT">{t("supply_types.GOODS_TRANSPORT", { defaultValue: "Goods transport (Art 49-50)" })}</option>
              {/* v0.6.11 (audit Tier 3 item 1) — Article 59's categories:
                  taxed where the customer is when sold to a consumer
                  outside the EU, ordinary Art 44/45 services otherwise. */}
              <option value="LICENCES_AND_RIGHTS">{t("ui.admin_clients.form.supply_type_option.LICENCES_AND_RIGHTS", { defaultValue: "Copyright / licences (Art 59(a))" })}</option>
              <option value="ADVERTISING">{t("ui.admin_clients.form.supply_type_option.ADVERTISING", { defaultValue: "Advertising (Art 59(b))" })}</option>
              <option value="CONSULTANCY_AND_INFORMATION">{t("ui.admin_clients.form.supply_type_option.CONSULTANCY_AND_INFORMATION", { defaultValue: "Consultancy or information (Art 59(c))" })}</option>
              <option value="REFRAIN_FROM_ACTIVITY">{t("ui.admin_clients.form.supply_type_option.REFRAIN_FROM_ACTIVITY", { defaultValue: "Refraining from an activity (Art 59(d))" })}</option>
              <option value="BANKING_FINANCIAL_INSURANCE">{t("ui.admin_clients.form.supply_type_option.BANKING_FINANCIAL_INSURANCE", { defaultValue: "Banking, financial or insurance (Art 59(e))" })}</option>
              <option value="STAFF_SUPPLY">{t("ui.admin_clients.form.supply_type_option.STAFF_SUPPLY", { defaultValue: "Supplying staff (Art 59(f))" })}</option>
              <option value="MOVABLE_PROPERTY_HIRE">{t("ui.admin_clients.form.supply_type_option.MOVABLE_PROPERTY_HIRE", { defaultValue: "Hiring out equipment (Art 59(g))" })}</option>
              <option value="UTILITY_NETWORK_ACCESS">{t("ui.admin_clients.form.supply_type_option.UTILITY_NETWORK_ACCESS", { defaultValue: "Gas / electricity network access (Art 59(h))" })}</option>
            </select>
          </div>
        </FormSection>
        <FormSection num="8" title={t("ui.admin_clients.form.fx_methods.title", { defaultValue: "Exchange-rate method" })}><ClientFxMethods form={form} setFxMethods={(v) => f("fx_methods", v)} /></FormSection>

        <div className="flex items-center gap-3 pt-2">
          <button data-testid="client-form-save" onClick={submit}
                  className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs px-5 py-3 rounded-xl">
            {isNew ? t("ui.admin_clients.form.create", { defaultValue: "Create entity" }) : t("ui.admin_clients.form.save_changes", { defaultValue: "Save changes" })}
          </button>
          <button onClick={onCancel}
                  className="border border-slate-300 dark:border-slate-700 text-xs px-5 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800">
            {t("ui.admin_clients.form.cancel", { defaultValue: "Cancel" })}
          </button>
        </div>
      </div>
    </div>
  );
};

const FormSection = ({ num, title, children }) => (
  <section className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4">
    <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50 flex items-baseline gap-3">
      <span className="text-teal-700 dark:text-teal-300 text-sm">{num}</span>
      <span>{title}</span>
    </h2>
    {children}
  </section>
);

// v0.6 item 2 — seller_establishment is auto-derived from establishment
// country (EU↔NON_EU); show it as a read-only derived value with an
// explicit override for the rare manual case (e.g. NON_EU_PAN_EU_FBA).
const DerivedEstablishment = ({ value, establishmentCountry, override,
                                onOverride, onChange }) => {
  const { t } = useTranslation();
  const cc = (establishmentCountry || "").toUpperCase();
  if (override) {
    return (
      <div className="space-y-1.5">
        <label className="text-[11px] tracking-normal text-slate-600 dark:text-slate-300">
          {t("ui.admin_clients.form.seller_est_overridden", { defaultValue: "Seller establishment (overridden)" })}
        </label>
        <select data-testid="client-form-est" value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-11 bg-white dark:bg-slate-900 border border-amber-400 dark:border-amber-600 focus:outline-none text-slate-900 dark:text-slate-50 px-4 text-sm rounded-md">
          {SE.map((o) => <option key={o} value={o}>{SE_LABEL[o] || o}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] tracking-normal text-slate-600 dark:text-slate-300">
        {t("ui.admin_clients.form.seller_est", { defaultValue: "Seller establishment" })}
      </label>
      <div data-testid="client-form-est-derived"
           className="w-full h-11 flex items-center gap-2 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 px-4 text-sm rounded-md">
        <span className="font-semibold">{SE_LABEL[value] || value}</span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {t("ui.admin_clients.form.seller_est_derived", { cc: cc ? ` (${cc})` : "", defaultValue: `— derived from Establishment country${cc ? ` (${cc})` : ""}` })}
        </span>
        <button type="button" data-testid="client-form-est-override"
                onClick={onOverride}
                className="ml-auto text-[11px] text-teal-700 dark:text-teal-300 hover:underline">
          {t("ui.admin_clients.form.seller_est_override", { defaultValue: "override" })}
        </button>
      </div>
    </div>
  );
};

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const Input = ({ label, value, onChange, testid, placeholder, hint,
                 required, type = "text", maxLength }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] tracking-normal text-slate-600 dark:text-slate-300 flex items-center gap-1">
      {label}{required && <span className="text-rose-500 dark:text-rose-400">{" *"}</span>}
      {hint && <InfoHint label={hint} testid={testid ? `${testid}-info` : "field-info"} />}
    </label>
    <input
      data-testid={testid} value={value} type={type} maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-11 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 focus:border-teal-500 dark:focus:border-teal-400 focus:outline-none text-slate-900 dark:text-slate-50 px-4 text-sm rounded-md"
    />
  </div>
);

const Select = ({ label, value, options, labels, onChange, testid, required }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] tracking-normal text-slate-600 dark:text-slate-300">
      {label}{required && <span className="text-rose-500 dark:text-rose-400">{" *"}</span>}
    </label>
    <select data-testid={testid} value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-11 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 focus:border-teal-500 dark:focus:border-teal-400 focus:outline-none text-slate-900 dark:text-slate-50 px-4 text-sm rounded-md">
      {options.map((o) => <option key={o || "_blank"} value={o}>{(labels && labels[o]) || o || "—"}</option>)}
    </select>
  </div>
);

const Note = ({ children, tone = "amber", testid }) => {
  const klass = tone === "amber"
    ? "bg-amber-50 dark:bg-amber-900/40 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100"
    : "bg-slate-100/50 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400";
  return (
    <div data-testid={testid}
         className={`text-sm px-4 py-3 rounded-md border ${klass}`}>
      {children}
    </div>
  );
};

const CodeRenameModal = ({ from, to, onConfirm, onCancel }) => { const { t } = useTranslation(); return (
  <div data-testid="code-rename-modal"
       className="bg-amber-50 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700/60 p-4 rounded-md text-sm text-amber-900 dark:text-amber-100 space-y-3">
    <div>
      {t("ui.admin_clients.form.rename_before", { defaultValue: "You are changing the code from" })} <b>{from}</b> {t("ui.admin_clients.form.rename_mid", { defaultValue: "to" })} <b>{to}</b>{t("ui.admin_clients.form.rename_after", { defaultValue: ". Existing filing references for this client will NOT be retroactively renamed — historical references are immutable for audit. New filings will use the new code prefix." })}
    </div>
    <div className="flex gap-2">
      <button data-testid="code-rename-confirm" onClick={onConfirm}
              className="bg-amber-600 text-white font-bold text-[11px] tracking-normal px-4 py-2 rounded">
        {t("ui.admin_clients.form.rename_confirm", { defaultValue: "Confirm rename" })}
      </button>
      <button onClick={onCancel}
              className="border border-slate-300 dark:border-slate-700 text-[11px] tracking-normal px-4 py-2 rounded text-slate-500 dark:text-slate-400">
        {t("ui.admin_clients.form.cancel", { defaultValue: "Cancel" })}
      </button>
    </div>
  </div>
); };
